import { marked } from 'marked';
import DOMPurify from 'dompurify';

export interface NewsPostMeta {
  title: string;
  date: string;
  author: string;
  authorUrl?: string;
  authorAvatar?: string;
  tags: string[];
  summary: string;
  coverImage?: string;
  featured?: boolean;
}

export interface NewsPost extends NewsPostMeta {
  slug: string;
  readingTime: string;
  rawContent: string;
  htmlContent: string;
}

export class NewsService {
  private static postsCache: NewsPost[] | null = null;

  private static parseFrontmatter(raw: string): { meta: Partial<NewsPostMeta>; body: string } {
    const frontmatterRegex = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/;
    const match = raw.match(frontmatterRegex);

    if (!match) {
      return { meta: {}, body: raw };
    }

    const yamlBlock = match[1];
    const body = match[2];
    const meta: Record<string, any> = {};

    const lines = yamlBlock.split('\n');
    for (const line of lines) {
      const colonIdx = line.indexOf(':');
      if (colonIdx === -1) continue;

      const key = line.slice(0, colonIdx).trim();
      let value = line.slice(colonIdx + 1).trim();

      // Handle strings in quotes
      if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }

      // Handle boolean
      if (value.toLowerCase() === 'true') {
        meta[key] = true;
      } else if (value.toLowerCase() === 'false') {
        meta[key] = false;
      } else if (value.startsWith('[') && value.endsWith(']')) {
        // Handle array like ["Release", "Packaging"]
        const items = value
          .slice(1, -1)
          .split(',')
          .map(item => {
            const clean = item.trim();
            return (clean.startsWith('"') && clean.endsWith('"')) || (clean.startsWith("'") && clean.endsWith("'"))
              ? clean.slice(1, -1)
              : clean;
          })
          .filter(Boolean);
        meta[key] = items;
      } else {
        meta[key] = value;
      }
    }

    return {
      meta: {
        title: meta.title || 'Untitled Post',
        date: meta.date || '2026-08-25',
        author: meta.author || 'Kim Schulz',
        authorUrl: meta.authorUrl || 'https://schulz.dk',
        authorAvatar: meta.authorAvatar || '/images/kim-schulz.png',
        tags: Array.isArray(meta.tags) ? meta.tags : ['Updates'],
        summary: meta.summary || '',
        coverImage: meta.coverImage || undefined,
        featured: Boolean(meta.featured),
      },
      body
    };
  }

  private static calculateReadingTime(text: string): string {
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  }

  public static getPosts(): NewsPost[] {
    if (this.postsCache) {
      return this.postsCache;
    }

    // Vite eager glob import of all markdown news files
    const rawFiles = import.meta.glob('/content/news/*.md', { query: '?raw', eager: true }) as Record<string, string | { default: string }>;
    const posts: NewsPost[] = [];

    for (const [path, contentObj] of Object.entries(rawFiles)) {
      const rawText = typeof contentObj === 'string' ? contentObj : (contentObj.default || '');
      const filename = path.split('/').pop()?.replace(/\.md$/, '') || 'post';
      const slug = filename;

      const { meta, body } = this.parseFrontmatter(rawText);
      const readingTime = this.calculateReadingTime(body);

      // Render markdown safely
      const parsedHtml = marked.parse(body) as string;
      const sanitizedHtml = DOMPurify.sanitize(parsedHtml, {
        ADD_ATTR: ['target', 'rel', 'id']
      });

      posts.push({
        slug,
        title: meta.title || slug.replace(/-/g, ' '),
        date: meta.date || '2026-08-25',
        author: meta.author || 'Kim Schulz',
        authorUrl: meta.authorUrl,
        authorAvatar: meta.authorAvatar,
        tags: meta.tags || ['Updates'],
        summary: meta.summary || '',
        coverImage: meta.coverImage,
        featured: meta.featured,
        readingTime,
        rawContent: body,
        htmlContent: sanitizedHtml,
      });
    }

    // Sort descending by date
    posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    this.postsCache = posts;
    return posts;
  }

  public static getPostBySlug(slug: string): NewsPost | null {
    const posts = this.getPosts();
    const cleanSlug = slug.toLowerCase().trim();
    return posts.find(p => p.slug.toLowerCase() === cleanSlug) || null;
  }

  public static getFeaturedPost(): NewsPost | null {
    const posts = this.getPosts();
    if (posts.length === 0) return null;
    return posts.find(p => p.featured) || posts[0];
  }

  public static getRecentPosts(limit: number = 3): NewsPost[] {
    const posts = this.getPosts();
    return posts.slice(0, limit);
  }

  public static getAllTags(): { tag: string; count: number }[] {
    const posts = this.getPosts();
    const map = new Map<string, number>();

    for (const p of posts) {
      for (const t of p.tags) {
        map.set(t, (map.get(t) || 0) + 1);
      }
    }

    return Array.from(map.entries()).map(([tag, count]) => ({ tag, count }));
  }

  public static filterPosts(query?: string, tag?: string): NewsPost[] {
    let posts = this.getPosts();

    if (tag && tag !== 'All') {
      posts = posts.filter(p => p.tags.some(t => t.toLowerCase() === tag.toLowerCase()));
    }

    if (query && query.trim()) {
      const q = query.toLowerCase().trim();
      posts = posts.filter(p => 
        p.title.toLowerCase().includes(q) ||
        p.summary.toLowerCase().includes(q) ||
        p.rawContent.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      );
    }

    return posts;
  }

  public static getAdjacentPosts(slug: string): { prev: NewsPost | null; next: NewsPost | null } {
    const posts = this.getPosts();
    const idx = posts.findIndex(p => p.slug.toLowerCase() === slug.toLowerCase());

    if (idx === -1) {
      return { prev: null, next: null };
    }

    // Because sorted descending: next in timeline is idx - 1 (newer), prev is idx + 1 (older)
    const prev = idx < posts.length - 1 ? posts[idx + 1] : null;
    const next = idx > 0 ? posts[idx - 1] : null;

    return { prev, next };
  }
}
